// shared-lib/cabinet-runs/newRunFamily.js
//
// The fields a NEW run is born with under the company's cabinet line (design
// 2026-10-05, D9): the family, and — when the room's look is not one the
// family sells — a look of its own that it does, the same carry a family
// switch makes. The drawing tool and the kitchen generator both read this, so
// a drawn run and a generated one cannot disagree. Pure.

const { familyById, DEFAULT_FAMILY_ID } = require('./families');
const { offeringOf, lookForFamily } = require('./familyOffering');
const { sameStoredValue } = require('../rooms/sameStoredValue');

const LOOK_KEYS = [
  'doorDesign',
  'doorStyle',
  'paintColor',
  'paintFinish',
  'texture',
];
/**
 * Two cabinet looks name the same doors, paint and wood. A look stored on a
 * room comes back from Firestore with its keys reordered, so each field is
 * compared with `sameStoredValue`, never as text — as text, an unchanged look
 * read as different and a run was given a redundant look of its own.
 */
const sameLook = (a, b) =>
  LOOK_KEYS.every((k) => sameStoredValue(a?.[k] ?? null, b?.[k] ?? null));

/**
 * The family a new run is built in for this company: its cabinet line
 * (`settings.cabinetLine`) when that names a family, else the line a run gets
 * with none set — "Not set (generic)" in the price book's settings, which a
 * run with no `family` reads as (`familyOf`). The one answer to "what line
 * does this user build in"; the closest-design kitchen (option #1) reads it
 * rather than naming a line of its own, so it and every first edit of it
 * follow what the user chose (user ruling 2026-10-23).
 */
function familyIdForNewRun(cabinetLine) {
  return familyById(cabinetLine) ? cabinetLine : DEFAULT_FAMILY_ID;
}

/**
 * `{ family, appearance? }` for a new run in `room` under the line
 * `familyId`; `{}` with no line or one that is not a family. The look is one
 * the company offers of the line (`companyOffering`, issue #2460 — the
 * standard package): the family's default where offered, else the first.
 *
 * `keepRoomLook: false` is a new kitchen the user asked NOT to dress in the
 * room's look (the generator's "copy from the current cabinets" question):
 * the run starts from no look — the line's default, or the plain default
 * with no line — and STATES it, because a run with none of its own wears the
 * room's (`effectiveAppearance`). An empty `appearance` is such a statement:
 * it wins that ladder and writes nothing. Only with nothing on either side —
 * no room look, no line default — is there nothing to state.
 */
function familyFieldsForNewRun(
  room,
  familyId,
  companyOffering,
  { keepRoomLook = true } = {}
) {
  const family = familyId ? familyById(familyId) : null;
  const roomLook = (room && room.cabinetAppearance) || {};
  if (!keepRoomLook) {
    const own = family
      ? lookForFamily({}, offeringOf(family, companyOffering))
      : {};
    const fields = family ? { family: family.id } : {};
    return Object.keys(roomLook).length || Object.keys(own).length
      ? { ...fields, appearance: own }
      : fields;
  }
  if (!family) return {};
  const mapped = lookForFamily(roomLook, offeringOf(family, companyOffering));
  return sameLook(mapped, roomLook)
    ? { family: family.id }
    : { family: family.id, appearance: mapped };
}

const LOOK_FIELDS = [
  'doorDesign',
  'doorStyle',
  'paintColor',
  'paintFinish',
  'texture',
];

/**
 * The look a new run of the line wears — its own carried look, or the room's
 * when that is already one the family sells — for objects built OUTSIDE the
 * engine (a generated island), which take no run appearance. Null with no
 * line. `opts` as `familyFieldsForNewRun`.
 */
function lookForNewRun(room, familyId, companyOffering, opts) {
  const fields = familyFieldsForNewRun(room, familyId, companyOffering, opts);
  if (!fields.family) return null;
  const look = fields.appearance || (room && room.cabinetAppearance) || {};
  return Object.fromEntries(
    LOOK_FIELDS.filter((k) => look[k] != null).map((k) => [k, look[k]])
  );
}

/** The look fields `obj` carries — what `wearLook` would hand another object. */
function lookOf(obj) {
  return Object.fromEntries(
    LOOK_FIELDS.filter((k) => obj?.[k] != null).map((k) => [k, obj[k]])
  );
}

/** `obj` wearing `look`: its look fields replaced, a texture dropped for paint. */
function wearLook(obj, look) {
  if (!look) return obj;
  const out = { ...obj };
  for (const k of LOOK_FIELDS) delete out[k];
  return { ...out, ...look };
}

module.exports = {
  LOOK_KEYS,
  sameLook,
  familyIdForNewRun,
  familyFieldsForNewRun,
  lookForNewRun,
  wearLook,
  lookOf,
};
